<template>
  <div
    ref="commentWrapper"
    class="flex flex-col gap-y-4"
    :class="(comment.isHighlighted || isHighlighted) && 'is-highlighted'"
  >
    <div class="flex items-center gap-x-4">
      <div class="flex flex-col gap-y-3">
        <Avatar size="sm">
          <AvatarImage :src="comment.avatarUrl || ''" alt="@unovue" />
        </Avatar>
      </div>
      <div class="flex w-full flex-col">
        <span class="mb-1 text-xs font-bold">{{ comment.name }}</span>
        <div class="text-sm" v-html="comment.comment"></div>
        <div class="mt-2 flex w-full justify-between">
          <div
            class="flex cursor-pointer select-none items-center gap-x-1 opacity-70 transition hover:opacity-100"
            @click="likeComment"
          >
            <Heart
              :size="12"
              :fill="isLiked ? 'red' : 'transparent'"
              :stroke="isLiked ? 'red' : 'black'"
            />
            <span class="text-xs">{{ likes }}</span>
          </div>
          <div class="flex items-center gap-x-4">
            <div
              class="flex cursor-pointer select-none items-center gap-x-1 opacity-70 transition hover:opacity-100"
              @click="handleEditComment"
            >
              <Pencil :size="12" />
              <span class="text-xs">{{ t('common.edit') }}</span>
            </div>
            <div
              class="flex cursor-pointer select-none items-center gap-x-1 opacity-70 transition hover:opacity-100"
              @click="handleDeleteComment"
            >
              <Trash2 :size="12" />
              <span class="text-xs">{{ t('common.delete') }}</span>
            </div>
            <div
              class="flex cursor-pointer select-none items-center gap-x-1 opacity-70 transition hover:opacity-100"
              @click="handleReportClick"
            >
              <Flag :size="12" />
              <span class="text-xs">{{ t('common.report') }}</span>
            </div>
            <div
              class="flex cursor-pointer select-none items-center gap-x-1 opacity-70 transition hover:opacity-100"
              @click="handleReplyClick"
            >
              <component
                :is="isRepliesFetching ? Loader2 : isReplyFormOpen && !isRepliedComment ? X : Reply"
                :class="isRepliesFetching && 'animate-spin'"
                :size="12"
              />
              <span class="text-xs"
                >{{ t('common.reply') }}
                <span v-if="!isRepliedComment">({{ comment.replyCount }})</span></span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCommentStore } from '@stores/comment.store';

// Interfaces
import type { CommentInterface } from '@modules/Comment/comment.interface';

// Components
import Avatar from '@ui/avatar/Avatar.vue';
import AvatarImage from '@ui/avatar/AvatarImage.vue';

// Icons
import { Heart, Reply, Flag, X, Loader2, Trash2, Pencil } from 'lucide-vue-next';

// Props
const props = defineProps<{
  comment: CommentInterface;
  isRepliesFetching?: boolean;
  isReplyFormOpen?: boolean;
  isReportFormOpen?: boolean;
  isSending?: boolean;
  isRepliedComment?: boolean;
}>();

// Composables
const { t } = useI18n();

// Stores
const commentStore = useCommentStore();

// Emits
const emit = defineEmits([
  'setReplyForm',
  'setReportForm',
  'clearReplies',
  'deleteComment',
  'setEditForm',
]);

// Refs
const likes = ref(props.comment.likeCount);
const isLiked = ref(props.comment.likedByMe);
const isReportCommentFormActive = ref(false);
const commentWrapper = ref<HTMLElement | null>(null);
const isHighlighted = ref(false);
const isCommentEditing = ref(false);

// Methods
const likeComment = async () => {
  if (!isLiked.value) {
    isLiked.value = true;
    likes.value = (likes.value || 0) + 1;

    await commentStore.likeComment(props.comment.id);
  } else {
    isLiked.value = false;
    likes.value = (likes.value || 0) - 1;

    await commentStore.deleteLikeComment(props.comment.id);
  }
};

const setReplyForm = () => {
  isReportCommentFormActive.value = false;
  commentStore.setReportCommentForm(false);
};

const setReportComment = () => {
  commentStore.storeReportData(
    props.comment.id || '',
    props.comment.name || '',
    props.comment.comment || '',
  );

  commentStore.setReportCommentForm(true);
  commentStore.clearReplyData();

  emit('clearReplies');

  isReportCommentFormActive.value = !isReportCommentFormActive.value;
};

const highlightComment = () => {
  isHighlighted.value = true;

  setTimeout(() => {
    isHighlighted.value = false;
  }, 1000);
};

const elementPositionInsideTheWrapper = (element: HTMLElement) => {
  if (element) {
    const position = element.offsetTop;
    const height = element.clientHeight;

    commentStore.storeCommentDialogScrollPosition(position - height);
    highlightComment();
  }
};

const handleReplyClick = () => {
  if (props.isReplyFormOpen && !props.isRepliedComment) {
    emit('clearReplies');
  } else {
    emit('setReplyForm');
  }
  elementPositionInsideTheWrapper(commentWrapper.value as HTMLElement);
  setReplyForm();
};

const handleReportClick = () => {
  elementPositionInsideTheWrapper(commentWrapper.value as HTMLElement);
  setReportComment();

  emit('setReportForm');
};

const handleDeleteComment = async () => {
  await commentStore.deleteLikeComment(props.comment.id);
  emit('deleteComment');
};

const handleEditComment = () => {
  emit('setEditForm');
  isCommentEditing.value = false;
};
</script>

<style>
.is-highlighted {
  @apply relative;
  @keyframes flash {
    from {
      background: #95ffbd;
    }
    to {
      background: transparent;
    }
  }
  &::before {
    content: '';
    @apply absolute -z-10 h-[calc(100%+1rem)] w-[calc(100%+3rem)];
    animation: flash 1s ease-in-out forwards;
    left: -1.5rem;
    top: -1rem;
  }
}
</style>
